import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPlannerSaveRecovery } from "../utils/plannerSaveRecovery";

// This hook never treats a current-row read as proof of request completion.
// Only the receipt transport can settle a request; row reads are used solely
// for an explicit user's reconciliation after all operations are terminal.
export default function usePlannerReceiptRecovery({
  storage, session, fetch, baseUrl, activePlanId, readCopy, writeCopy,
  contentFingerprint, currentContent, onCommitted,
}) {
  const callbacks = useRef({ readCopy, writeCopy, contentFingerprint, currentContent, onCommitted, activePlanId });
  callbacks.current = { readCopy, writeCopy, contentFingerprint, currentContent, onCommitted, activePlanId };
  const [notices, setNotices] = useState({});
  const recovery = useMemo(() => createPlannerSaveRecovery({
    storage, isCurrent: session.isCurrent, fetch, baseUrl,
    headers: () => ({ Authorization: `Bearer ${session.token}` }),
  }), [storage, session, fetch, baseUrl]);
  const updateNotice = useCallback((planId, notice) => {
    if (session.isCurrent()) setNotices((previous) => ({ ...previous, [planId]: notice }));
  }, [session]);
  const hasPending = useCallback((planId) => {
    try { return Boolean(planId && recovery.readPending(planId).length); }
    catch { return true; }
  }, [recovery]);
  const persistPatch = useCallback((planId, patch) => {
    if (!session.isCurrent()) throw new Error("Planner session changed.");
    const api = callbacks.current;
    api.writeCopy(planId, patch);
    const stored = api.readCopy(planId);
    if (!stored || Object.entries(patch).some(([key, value]) => JSON.stringify(stored[key]) !== JSON.stringify(value))) {
      throw new Error("The save result could not be retained in this browser. Check its outcome again before saving.");
    }
    return stored;
  }, [session]);
  const acknowledge = useCallback((planId, result) => {
    recovery.acknowledge(planId, result.request.requestId, result.requestHash);
  }, [recovery]);
  const applyResult = useCallback((planId, result, { recovered = false } = {}) => {
    if (!session.isCurrent()) return false;
    const api = callbacks.current;
    if (result.outcome === "COMMITTED") {
      const body = result.request.savedPayload;
      const sent = api.contentFingerprint({ formData: body.formData, strategy: body.strategy,
        creatives: body.creativeRecommendations, creativeMeta: body.creativeGenerationMeta });
      const copy = api.readCopy(planId);
      const visible = api.activePlanId === planId ? api.currentContent.current : null;
      const held = visible || { formData: copy?.formData, strategy: copy?.strategy,
        creatives: copy?.creativeRecommendations || copy?.creativeVariations, creativeMeta: copy?.creativeGenerationMeta };
      const otherPending = recovery.readPending(planId).some((record) => record.request.requestId !== result.request.requestId);
      const sameContent = !result.superseded && !otherPending && sent === api.contentFingerprint(held);
      // A later locally confirmed version cannot be moved backwards by an
      // older receipt. Its historical success remains true, not current Saved.
      const advanced = copy?.plannerWriteVersion && Number(copy.plannerWriteVersion) > Number(result.receipt.writeVersion);
      const needsReview = result.superseded || Boolean(advanced);
      persistPatch(planId, {
        ...(!copy?.formData ? { formData: body.formData, strategy: body.strategy || "",
          creativeRecommendations: body.creativeRecommendations || [],
          creativeVariations: body.creativeRecommendations || [], creativeGenerationMeta: body.creativeGenerationMeta || null } : {}),
        serverDraftId: String(result.receipt.serverDraftId),
        plannerWriteVersion: advanced ? copy.plannerWriteVersion : result.receipt.writeVersion,
        plannerReceiptId: result.receipt.requestId,
        plannerRecoveryRequired: needsReview,
        unsavedChanges: !sameContent || needsReview,
        serverSavedAt: result.draft?.savedAt || result.draft?.updatedAt || copy?.serverSavedAt || null,
        awaitingFirstExplicitSave: false,
        plannerExplicitSaveRequired: recovered && !sameContent,
      });
      acknowledge(planId, result);
      updateNotice(planId, needsReview ? { kind: "review", message: "This save completed, but the saved plan changed afterward. Your browser edits are preserved. Review the saved inputs before saving again." } : null);
      api.onCommitted?.({ planId, result, sentContent: sent, sameContent: sameContent && !needsReview });
      return !needsReview && !otherPending;
    }
    if (result.outcome === "CANCELLED" || result.outcome === "REJECTED") {
      // A first-save cancellation leaves no row to reconcile. A version
      // conflict (or an existing-row cancellation) needs explicit review.
      const needsReview = result.outcome === "CANCELLED"
        ? Boolean(result.request.savedPayload.serverDraftId)
        : ["PLANNER_WRITE_CONFLICT", "PLANNER_WRITE_VERSION_REQUIRED", "planner_local_draft_id_ambiguous"].includes(result.receipt?.code);
      persistPatch(planId, {
        ...(needsReview && result.draft?.writeVersion && String(result.draft.advertiserId) === String(session.advertiserId)
          ? { serverDraftId: String(result.draft.serverDraftId || result.draft.id) } : {}),
        plannerRecoveryRequired: needsReview, plannerExplicitSaveRequired: true, unsavedChanges: true,
      });
      acknowledge(planId, result);
      updateNotice(planId, { kind: needsReview ? "review" : "settled",
        message: needsReview ? "The save did not change the plan. Your browser edits are preserved. Review the saved inputs before saving again."
          : result.outcome === "CANCELLED" ? "The pending save was cancelled before it wrote. Your browser edits are preserved. You can save again." : "The save was refused without changing the plan. Correct the inputs and save again; your browser edits are preserved." });
      return false;
    }
    if (result.outcome === "BLOCKED") {
      updateNotice(planId, { kind: "blocked", message: ["PLANNER_REQUEST_TOO_LARGE", "PLANNER_REQUEST_INVALID"].includes(result.code) ? result.message : "The save was not sent because this browser could not safely retain it. Your inputs remain on this page; restore browser storage and secure access before trying again." });
      return false;
    }
    persistPatch(planId, { unsavedChanges: true });
    updateNotice(planId, { kind: "unknown", message: "The save outcome is not yet known. Your browser edits are preserved. Check its outcome before saving or generating this plan; other plans remain available." });
    return false;
  }, [session, recovery, persistPatch, acknowledge, updateNotice]);
  const recover = useCallback(async (planId) => {
    updateNotice(planId, { kind: "checking", message: "Checking the pending save…" });
    const recovered = await recovery.recover(planId);
    if (!session.isCurrent()) return;
    try {
      for (const result of recovered.results || []) applyResult(planId, result, { recovered: true });
      if (hasPending(planId)) updateNotice(planId, { kind: "unknown", message: "The save outcome is not yet known. Your browser edits are preserved. Check again when the service returns." });
    } catch (error) { updateNotice(planId, { kind: "unknown", message: error.message }); }
  }, [recovery, session, applyResult, hasPending, updateNotice]);
  const attempted = useRef(new Set());
  useEffect(() => {
    if (!activePlanId || attempted.current.has(activePlanId)) return;
    attempted.current.add(activePlanId);
    if (hasPending(activePlanId)) { recover(activePlanId); return; }
    if (readCopy(activePlanId)?.plannerRecoveryRequired) updateNotice(activePlanId, {
      kind: "review", message: "Your browser edits are preserved. Review the saved inputs before saving again.",
    });
  }, [activePlanId, hasPending, recover, readCopy, updateNotice]);
  const run = useCallback(async ({ planId, savedPayload, explicit = false }) => {
    const copy = callbacks.current.readCopy(planId);
    if (hasPending(planId)) {
      updateNotice(planId, { kind: "unknown", message: "Check the pending save outcome before saving or generating this plan." });
      return { outcome: "RECOVERY_REQUIRED" };
    }
    if (!explicit && copy?.plannerExplicitSaveRequired) return { outcome: "EXPLICIT_SAVE_REQUIRED" };
    const version = copy?.plannerWriteVersion;
    if (copy?.plannerRecoveryRequired || (savedPayload.serverDraftId && !version)) {
      updateNotice(planId, { kind: "review", message: "Review the saved inputs before saving these browser edits. Opening a saved plan does not save anything." });
      return { outcome: "REVIEW_REQUIRED" };
    }
    const result = await recovery.run({ planId, savedPayload,
      expectedWriteVersion: savedPayload.serverDraftId ? String(version) : null });
    if (result.outcome !== "SESSION_CHANGED") {
      try {
        const applied = applyResult(planId, result);
        if (result.outcome === "COMMITTED" && !applied) return { ...result, outcome: "REVIEW_REQUIRED" };
      }
      catch (error) { updateNotice(planId, { kind: "unknown", message: error.message }); return { ...result, outcome: "LOCAL_RECOVERY_REQUIRED" }; }
    }
    return result;
  }, [recovery, hasPending, applyResult, updateNotice]);
  const review = useCallback(async (planId) => {
    if (hasPending(planId)) return recover(planId);
    const copy = callbacks.current.readCopy(planId);
    if (!copy?.serverDraftId) {
      updateNotice(planId, { kind: "review", message: "This save was refused. Keep your browser copy and choose a valid saved plan before retrying." });
      return;
    }
    updateNotice(planId, { kind: "checking", message: "Loading the saved inputs for review…" });
    const result = await recovery.readDraft(copy.serverDraftId);
    if (result.outcome === "SESSION_CHANGED") return;
    if (result.outcome !== "READ") {
      updateNotice(planId, { kind: "review", message: "The saved inputs could not be verified. Your browser edits remain preserved; try again when the service returns." });
      return;
    }
    updateNotice(planId, { kind: "review", draft: result.draft, message: "Review these saved inputs. Keeping your browser edits prepares them to replace this version on your next save; it does not save now." });
  }, [recovery, hasPending, recover, updateNotice]);

  const keepBrowserEdits = useCallback((planId, draft) => {
    if (!session.isCurrent() || hasPending(planId) || !draft?.writeVersion) return;
    try {
      persistPatch(planId, { plannerWriteVersion: draft.writeVersion, plannerRecoveryRequired: false, plannerExplicitSaveRequired: true, unsavedChanges: true });
      updateNotice(planId, { kind: "settled", message: "Browser edits retained. Save when you are ready to replace the reviewed saved inputs." });
    } catch (error) { updateNotice(planId, { kind: "review", message: error.message }); }
  }, [session, hasPending, persistPatch, updateNotice]);
  const handle = useMemo(() => ({ run, recover, review, keepBrowserEdits, hasPending }),
    [run, recover, review, keepBrowserEdits, hasPending]);
  handle.notice = notices[activePlanId] || null;
  return handle;
}
